import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { P, rows, type Grade, type Location, type OrgUnit } from '@hrms/shared';
import { useAuth } from '../../../auth/AuthProvider';
import { api } from '../../../lib/api';
import { CrudList, type FieldSpec } from '../components/CrudList';
import { optLabel, stateOptions, useOrgOptions } from '../components/orgOptions';
import { Badge, PageHeader, Screen, Tabs, type Column } from '../../../ui';

type TabKey = 'legalEntities' | 'locations' | 'departments' | 'designations' | 'grades' | 'costCentres';

const codeName: FieldSpec[] = [
  { key: 'code', label: 'Code', required: true },
  { key: 'name', label: 'Name', required: true },
];
function baseCols<T extends OrgUnit>(): Column<T>[] {
  return [
    { key: 'code', title: 'Code', width: 100 },
    { key: 'name', title: 'Name', width: 180, flex: 2 },
  ];
}
const str = (v: unknown) => (v == null || v === '' ? '—' : String(v));

/** Org masters: legal entities, locations, departments, designations, grades, cost centres. */
export function OrgStructureScreen() {
  const { can } = useAuth();
  const edit = can(P.ORG_MANAGE);
  const [tab, setTab] = useState<TabKey>('legalEntities');
  const org = useOrgOptions();
  const cals = useQuery({ queryKey: ['holiday-calendars', 'all'], queryFn: () => api.leave.calendars.list({ per_page: 200 }) });
  const structs = useQuery({ queryKey: ['salary-structures', 'all'], queryFn: () => api.payroll.structures.list({ per_page: 200 }), enabled: can(P.PAYROLL_VIEW, P.PAYROLL_MANAGE) });
  const shifts = useQuery({ queryKey: ['shifts', 'all'], queryFn: () => api.attendance.shifts.list({ per_page: 200 }) });
  const calOpts = rows(cals.data).map((c) => ({ value: c.id, label: `${c.name} (${c.year})` }));
  const structOpts = rows(structs.data).map((s) => ({ value: s.id, label: s.name }));
  const shiftOpts = rows(shifts.data).map((s) => ({ value: s.id, label: `${s.name} (${s.start_time}–${s.end_time})` }));
  const parentField = (opts: { value: number; label: string }[]): FieldSpec => ({ key: 'parent_id', label: 'Parent', type: 'select', options: opts });

  return (
    <Screen testID="org-structure">
      <PageHeader title="Org structure" subtitle="Masters used across employee records, payroll and reporting." />
      <Tabs<TabKey>
        value={tab}
        onChange={setTab}
        tabs={[
          { key: 'legalEntities', label: 'Legal entities' },
          { key: 'locations', label: 'Locations' },
          { key: 'departments', label: 'Departments' },
          { key: 'designations', label: 'Designations' },
          { key: 'grades', label: 'Grades' },
          { key: 'costCentres', label: 'Cost centres' },
        ]}
      />
      {tab === 'legalEntities' && (
        <CrudList<OrgUnit>
          embedded
          canEdit={edit}
          title="Legal entities"
          singular="Legal entity"
          resource={api.org.legalEntities}
          queryKey={['org', 'legalEntities']}
          columns={[
            ...baseCols<OrgUnit>(),
            { key: 'pan', title: 'PAN', width: 120, render: (r) => str(r.pan) },
            { key: 'tan', title: 'TAN', width: 120, render: (r) => str(r.tan) },
            { key: 'pf_code', title: 'PF code', width: 130, render: (r) => str(r.pf_code) },
          ]}
          fields={[
            ...codeName,
            { key: 'pan', label: 'PAN' },
            { key: 'tan', label: 'TAN' },
            { key: 'gstin', label: 'GSTIN' },
            { key: 'pf_code', label: 'PF establishment code' },
            { key: 'esi_code', label: 'ESI code' },
            { key: 'address', label: 'Registered address', type: 'textarea' },
          ]}
        />
      )}
      {tab === 'locations' && (
        <CrudList<Location>
          embedded
          canEdit={edit}
          title="Locations"
          singular="Location"
          resource={api.org.locations}
          queryKey={['org', 'locations']}
          columns={[
            ...baseCols<Location>(),
            { key: 'state_code', title: 'State', width: 70 },
            { key: 'is_metro', title: 'Metro', width: 80, sortValue: (r) => (r.is_metro ? 1 : 0), render: (r) => (r.is_metro ? <Badge tone="info">Metro</Badge> : '—') },
            { key: 'legal_entity_id', title: 'Legal entity', width: 150, render: (r) => optLabel(org.legalEntities, r.legal_entity_id) },
            { key: 'holiday_calendar_id', title: 'Holiday calendar', width: 150, render: (r) => calOpts.find((o) => o.value === r.holiday_calendar_id)?.label ?? '—' },
            { key: 'geo_radius_m', title: 'Geofence', width: 90, render: (r) => (r.latitude != null ? `${r.geo_radius_m ?? 200} m` : '—') },
          ]}
          fields={[
            ...codeName,
            { key: 'state_code', label: 'State (for PT / LWF)', type: 'select', options: stateOptions, required: true },
            { key: 'legal_entity_id', label: 'Legal entity', type: 'select', options: org.legalEntities },
            { key: 'holiday_calendar_id', label: 'Holiday calendar', type: 'select', options: calOpts },
            { key: 'is_metro', label: 'Metro city (50% HRA exemption)', type: 'checkbox' },
            { key: 'address', label: 'Address', type: 'textarea' },
            { key: 'latitude', label: 'Latitude', type: 'number', hint: 'For geo-fenced punches' },
            { key: 'longitude', label: 'Longitude', type: 'number' },
            { key: 'geo_radius_m', label: 'Geofence radius (m)', type: 'number', default: 200 },
          ]}
        />
      )}
      {tab === 'departments' && (
        <CrudList<OrgUnit>
          embedded
          canEdit={edit}
          title="Departments"
          singular="Department"
          resource={api.org.departments}
          queryKey={['org', 'departments']}
          columns={[...baseCols<OrgUnit>(), { key: 'parent_id', title: 'Parent', width: 160, render: (r) => optLabel(org.departments, r.parent_id) }]}
          fields={[...codeName, parentField(org.departments)]}
        />
      )}
      {tab === 'designations' && (
        <CrudList<OrgUnit> embedded canEdit={edit} title="Designations" singular="Designation" resource={api.org.designations} queryKey={['org', 'designations']} columns={baseCols<OrgUnit>()} fields={codeName} />
      )}
      {tab === 'grades' && (
        <CrudList<Grade>
          embedded
          canEdit={edit}
          title="Grades"
          singular="Grade"
          resource={api.org.grades}
          queryKey={['org', 'grades']}
          columns={[
            ...baseCols<Grade>(),
            { key: 'salary_structure_id', title: 'Salary structure', width: 160, render: (r) => structOpts.find((s) => s.value === r.salary_structure_id)?.label ?? '—' },
            { key: 'default_shift_id', title: 'Default shift', width: 160, render: (r) => shiftOpts.find((s) => s.value === r.default_shift_id)?.label ?? '—' },
          ]}
          fields={[
            ...codeName,
            { key: 'salary_structure_id', label: 'Default salary structure', type: 'select', options: structOpts, hint: structOpts.length ? undefined : 'Requires payroll access to list structures' },
            { key: 'default_shift_id', label: 'Default shift', type: 'select', options: shiftOpts },
          ]}
        />
      )}
      {tab === 'costCentres' && (
        <CrudList<OrgUnit>
          embedded
          canEdit={edit}
          title="Cost centres"
          singular="Cost centre"
          resource={api.org.costCentres}
          queryKey={['org', 'costCentres']}
          columns={[...baseCols<OrgUnit>(), { key: 'parent_id', title: 'Parent', width: 160, render: (r) => optLabel(org.costCentres, r.parent_id) }]}
          fields={[...codeName, parentField(org.costCentres)]}
        />
      )}
    </Screen>
  );
}
